<template>
  <div class="container">
    <donut-page-title :title="`${startCase($i18n.t('shoutouts.rewards_store'))}`" />
    <div v-if="!walletIsActive" class="m-vx2">
      <donut-alert
        title="Your wallet is disabled"
        subtitle="Ask your admin to enable your wallet again to use your points."
      />
    </div>
    <div class="flex">
      <div class="f-v f-jc f-gap-8 w-min-50p w-max-50p">
        <div>
          <div
            v-emoji="`
            You have ${currentRedeemable.toLocaleString()}
            ${currentRedeemable === 1 ? pointsConfig.points_name : pointsConfig.points_name_plural}
            :${pointsConfig.emoji}: to spend on ${$i18n.t('shoutouts.reward', { count: 2 })}!
            `"
            class="t m-bx1"
          />
          <div v-if="currentRedeemable > 0" class="t m-bx1">
            Redeem them now — or save for something bigger later.
          </div>
          <a v-if="userIsAdmin" class="button" href="/rewards/settings">
            Add and Edit {{ startCase($i18n.t('shoutouts.reward', { count: 2 })) }}
          </a>
        </div>
      </div>
      <div class="f-v f-gap-8 w-min-50p w-max-50p p-b14 f-ae">
        <shoutouts-profiles-stat-box
          :title="`${startCase(pointsConfig.points_name)} Balance`"
          :stat="currentRedeemable"
          :emoji="`${pointsConfig.emoji}`"
          :disabled="!walletIsActive"
          icon="Wallet"
          :primary="true"
        />
      </div>
    </div>
    <div v-if="state.rewardsListings.description" class="m-bx2">
      <donut-alert :subtitle="state.rewardsListings.description" theme="info" />
    </div>
    <div class="m-bx2">
      <div class="lattice">
        <shoutouts-rewards-store-listing
          v-for="listing in state.rewardsListings.listings.filter((listing) => listing.state === 'active')"
          :key="listing.id"
          :points-config="pointsConfig"
          :listing="listing"
          :disabled="!walletIsActive"
        />
      </div>
    </div>
    <!-- Should display if user is admin and gift card does not exist or listing is not active -->
    <div v-if="userIsAdmin && (!giftCardListing || giftCardListing.state !== 'active')" class="m-bx2">
      <div class="separator m-bx2" />
      <shoutouts-programs-gift-card-toggle
        :points-config="pointsConfig"
        :dollars-per-point="dollarsPerPoint"
        :gift-card-listing="giftCardListing"
      />
    </div>
    <!-- Shoutouts promotional section - only shown when Shoutouts is not active -->
    <div v-if="!activeShoutoutsProgram" class="m-bx2">
      <div class="separator m-bx2" />
      <div class="t-xl t-bold m-bx1">{{ featureName }}</div>
      <div class="f-sb m-bx2">
        <div class="t w-max-60p">
          <span>{{ shoutoutsAdCopy }}</span>
          <span v-if="premiumAccount" class="t-italic">{{ featureName }} is included in your plan.</span>
        </div>
        <div class="f f-gap-8">
          <a
            href="https://help.donut.ai/en/articles/11368218-getting-started-donut-shoutouts"
            target="_blank"
            class="button inv"
          >How it works</a>
          <a v-if="isServiceAccountManager" href="/shoutouts" class="button">Go to {{ featureName }}</a>
          <button v-else class="button" @click="openAdminModal">Tell your admin</button>
        </div>
      </div>
      <cdn-img class="w-100p" path="graphics/shoutouts/landing-example" />
    </div>
  </div>
</template>

<script setup>
  import { inject, computed } from 'vue';
  import { useStore } from 'vuex';
  import { startCase } from 'lodash';

  const $i18n = inject('$i18n');
  const { state, commit } = useStore();

  const pointsConfig = computed(() => state.pointsConfig.config);
  const dollarsPerPoint = computed(() => state.pointsConfig.dollarsPerPoint);
  const giftCardListing = computed(() => state.rewardsListings.listings.find((listing) => listing.is_gift_card === true));
  const currentRedeemable = computed(() => state.rewardsListings.pointsWallet.current_redeemable);
  const walletIsActive = computed(() => state.rewardsListings.pointsWallet.active === true);
  const userIsAdmin = computed(() => state.currentUser.points_admin);

  // Shoutouts promotional section
  const featureName = $i18n.t('shoutouts.feature');
  const activeShoutoutsProgram = computed(() => state.shoutoutsProgram.activeShoutoutsProgram === true);
  const admin = computed(() => state.shoutoutsProgram.admin);
  const premiumAccount = computed(() => state.currentTeam.highest_plan_name === 'Premium');
  const isServiceAccountManager = computed(() => state.shoutoutsProgram.isServiceAccountManager === true);
  const shoutoutsAdCopy = `Get and give recognition points peer-to-peer in Slack. To get started, let your admin know you're interested in ${featureName}.`;

  const openAdminModal = () => {
    commit('updateModal', {
      visible: true,
      subcomponent: 'shoutouts-rewards-modals-tell-admin',
      data: { admin: admin.value },
    });
  };
</script>
